import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AdminTab from "../components/AdminTab.jsx";
import ChangeLogsTab from "../components/ChangeLogsTab.jsx";
import ContentCard from "../components/ContentCard";
import DetailsHeader from "../components/DetailsHeader";
import ExpiringAccessTab, { computeExpiringCandidates } from "../components/ExpiringAccessTab.jsx";
import PermissionAuditTab from "../components/PermissionAuditTab.jsx";
import UsersTab from "../components/UsersTab.jsx";
import "./ProjectDetails.css";

import { detailCardSx, detailItemSx, detailsGridSx } from "../styles/detailsLayout";
import { getCompanyId } from "../utils/api";
import { apiRequest } from "../utils/apiRequest";
import {
  ensureCompanyUsers,
  ensureProjectWithUsers,
  getProjectById,
  refreshForCurrentRoute,
  refreshProjectUsersForCompany,
} from "../utils/sessionStorageLayer";
import { showToast } from "../utils/toastOptions";
import { useEventStream } from "../utils/useEventStream";
import { usePermissionTemplate } from "../utils/usePermissionTemplate";
import { useThresholdDays } from "../utils/useThresholdDays";
import { useUserSession } from "../utils/useUserSession";

export default function ProjectDetails() {
  const { companyId: companyIdParam, projectId: projectIdParam } = useParams();
  const companyId = Number(companyIdParam);
  const projectId = Number(projectIdParam);

  const { user, loading: userLoading, error: userError } = useUserSession();

  // Fetch permission template from backend (not stored client-side)
  const { template: permissionTemplate } = usePermissionTemplate();

  const [project, setProject] = useState(null);
  const [projectUsers, setProjectUsers] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [changeLogRefreshKey, setChangeLogRefreshKey] = useState(0);
  const [changeLogLiveUpdate, setChangeLogLiveUpdate] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([0]));
  const handleTabChange = (_e, newValue) => {
    setTabIndex(newValue);
    setVisitedTabs((prev) => {
      if (prev.has(newValue)) return prev;
      const next = new Set(prev);
      next.add(newValue);
      return next;
    });
  };

  const [loadingAction, setLoadingAction] = useState(null);

  const [thresholdDaysCompanyDefault, setThresholdDaysCompanyDefault] = useState(null);
  const {
    thresholdDays: thresholdDaysProject,
    setThresholdDays: setThresholdDaysProject,
    inputDays,
    setInputDays,
    showConfirm,
    handleInputChange,
    handleSubmit,
    handleCancel: handleCancelBase,
    updateThresholdDays,
  } = useThresholdDays({ variant: "project" });
  const handleConfirm = async () => {
    await updateThresholdDays(project);
  };
  const handleReset = () => {
    setInputDays(thresholdDaysCompanyDefault);
    handleSubmit();
  };
  const handleCancel = () => {
    setInputDays(thresholdDaysProject);
    handleCancelBase();
  };

  const isAdmin = useMemo(() => {
    return permissionTemplate?.[project?.company?.id]?.user_access_levels?.admin === 4;
  }, [permissionTemplate, project]);

  // Expiring Access scope: only consider users currently assigned to this
  // project. Cross-reference project-user IDs with the company-users payload
  // so we get the fields (last_login_at, is_whitelisted) needed for the rules.
  const projectAssignedCompanyUsers = useMemo(() => {
    if (!Array.isArray(companyUsers) || !companyUsers.length) return [];
    if (!Array.isArray(projectUsers) || !projectUsers.length) return [];
    const ids = new Set(projectUsers.map((u) => u?.id).filter((id) => id != null));
    return companyUsers.filter((u) => ids.has(u?.id));
  }, [companyUsers, projectUsers]);

  const handleRestoreUser = useCallback(
    async (userId) => {
      if (loadingAction) return;
      if (!user?.daitaflow_user_id) {
        showToast("error", "Not signed in.");
        return;
      }
      setLoadingAction("restoreUser");
      try {
        await apiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/companies/${companyId}/users/reactivate/${userId}/`,
          {
            method: "PATCH",
            body: {
              target_user_id: userId,
            },
          }
        );
        showToast("success", "User reactivated successfully");
        await refreshProjectUsersForCompany(companyId, true);
        const updated = getProjectById(companyId, projectId);
        if (updated) {
          setProject(updated);
          setProjectUsers(updated.users || []);
        }
      } catch (error) {
        showToast("error", error.message || "Failed to reactivate user");
      } finally {
        setLoadingAction(null);
      }
    },
    [companyId, loadingAction, projectId, user?.daitaflow_user_id]
  );

  // Refresh: layer re-fetches project + users and merges into storage; we re-read and update state
  const refreshStorageData = async () => {
    if (loadingAction) return;
    setLoadingAction("refresh");
    try {
      await refreshForCurrentRoute(window.location.pathname);
      const p = getProjectById(companyId, projectId);
      if (p) {
        setProject(p);
        setProjectUsers(p.users || []);
      }
    } catch (error) {
      showToast("error", error.message || "Failed to refresh storage data");
    } finally {
      setLoadingAction(null);
    }
  };

  const fetchData = async (forceRefresh = false) => {
    if (!user) {
      setError("User session not available.");
      setLoading(false);
      return;
    }

    try {
      await ensureProjectWithUsers(companyId, projectId, forceRefresh, true);
    } catch (error) {
      showToast("error", error.message || "Failed to load project");
    }

    const foundProject = getProjectById(companyId, projectId);
    setProject(foundProject);
    setProjectUsers(foundProject?.users || []);

    if (!foundProject) {
      setError("Project not found");
      showToast("error", "Project not found");
      setLoading(false);
      return;
    }

    if (isAdmin && foundProject) {
      try {
        const companyId = getCompanyId(foundProject);
        if (!companyId) {
          showToast("error", "Project missing Company ID");
          return;
        }
        try {
          const data = await apiRequest(
            `${process.env.REACT_APP_API_BASE_URL}/projects/${foundProject.id}/inactive-days-threshold/`,
            { method: "GET", forceRefresh, suppressErrorToast: true }
          );
          setThresholdDaysProject(data.project_inactive_days_threshold);
          setThresholdDaysCompanyDefault(data.company_inactive_days_threshold);
          setInputDays(data.project_inactive_days_threshold);
        } catch (err) {
          if (err?.status === 404) {
            await updateThresholdDays(foundProject);
          } else {
            showToast("error", err?.message || "Failed to fetch inactive days threshold");
          }
        }

        // Company users power the Expiring Access tab (needs is_whitelisted
        // and last_login_at, neither of which the project-users endpoint
        // returns). Cached in sessionStorage — only hits the backend on a
        // cold cache or explicit refresh.
        try {
          const users = await ensureCompanyUsers(companyId, forceRefresh);
          setCompanyUsers(users);
        } catch (err) {
          showToast("error", err?.message || "Failed to load company users");
        }
      } catch (error) {
        showToast("error", error.message || "Failed to fetch admin data");
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!userLoading) {
      // Only show spinner on initial load or route change, not when isAdmin updates after project loads
      const alreadyHasProject = project?.id === projectId;
      if (!alreadyHasProject) {
        setLoading(true);
        setError(null);
      }
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, userLoading, isAdmin]);

  const companyIdForChangeLog = getCompanyId(project);

  const changeLogAffectsProjectUsers = useCallback((log) => {
    const actions = ["Add to Project", "Remove from Project", "Deactivate User"];
    return (log?.entries || []).some((e) => actions.includes(e?.action));
  }, []);

  useEventStream({
    companyIds: companyIdForChangeLog ? [companyIdForChangeLog] : [],
    enabled: !!user && !!companyIdForChangeLog && isAdmin,
    onConnected: () => setChangeLogRefreshKey((k) => k + 1),
    onChangeLogUpdated: async (data) => {
      if (Number(data?.company_id) !== Number(companyIdForChangeLog)) return;
      if (data.log) {
        setChangeLogLiveUpdate({ log: data.log, ts: Date.now() });
        if (changeLogAffectsProjectUsers(data.log)) {
          await refreshProjectUsersForCompany(companyIdForChangeLog, true);
          const updated = getProjectById(companyIdForChangeLog, projectId);
          if (updated) {
            setProject(updated);
            setProjectUsers(updated?.users || []);
          }
        }
      } else {
        setChangeLogRefreshKey((k) => k + 1);
        await refreshProjectUsersForCompany(companyIdForChangeLog, true);
        const updated = getProjectById(companyIdForChangeLog, projectId);
        if (updated) {
          setProject(updated);
          setProjectUsers(updated?.users || []);
        }
      }
    },
  });

  const expiringCount = useMemo(() => {
    if (!isAdmin || !thresholdDaysCompanyDefault) return null;
    return computeExpiringCandidates({
      companyUsers: projectAssignedCompanyUsers,
      projects: project ? [project] : [],
      companyThreshold: thresholdDaysCompanyDefault,
    }).length;
  }, [isAdmin, thresholdDaysCompanyDefault, projectAssignedCompanyUsers, project]);

  // Show error toast when errors occur
  useEffect(() => {
    if (error) {
      showToast("error", error);
    }
  }, [error]);

  if (userLoading || isLoading)
    return (
      <div className="spinner-overlay" role="status" aria-label="Loading">
        <div className="spinner"></div>
      </div>
    );
  if (!project) {
    return (
      <div className="page">
        <div className="project-details-container">
          <Typography
            variant="h6"
            sx={{ padding: "2rem", textAlign: "center", color: "text.secondary" }}
          >
            Project not found
          </Typography>
        </div>
      </div>
    );
  }

  const renderDetail = (label, value) => {
    if (value == null || value === "") return null;
    let displayValue = value;
    if (typeof value === "boolean")
      displayValue = value ? (
        <span style={{ color: "green" }}>Yes</span>
      ) : (
        <span style={{ color: "red" }}>No</span>
      );
    else if (label.includes("Date") || label.includes("At")) {
      const d = new Date(value);
      displayValue = (
        <Box
          component="span"
          sx={{ display: "flex", flexDirection: "column", paddingLeft: "10px" }}
        >
          <span>{d.toLocaleDateString()}</span>
          <span>{d.toLocaleTimeString()}</span>
        </Box>
      );
    }
    return (
      <Box sx={detailItemSx}>
        <span className="detail-label">{label}:</span>
        <span className="detail-value">{displayValue}</span>
      </Box>
    );
  };

  return (
    <div className="page">
      <DetailsHeader
        title={`Project: ${project.name}`}
        onBack={() => window.history.back()}
        onRefresh={async () => {
          await refreshStorageData();
          await fetchData(true);
        }}
        sx={{ margin: "0.5rem 0 2rem" }}
      >
        <Box sx={detailsGridSx}>
          {renderDetail("ID", project.id)}
          {renderDetail("Active", project.active)}
          {renderDetail("Total Users", projectUsers.length)}
          {expiringCount !== null && renderDetail("Expiring Access", expiringCount)}
          {renderDetail("Address", project.address)}
          {renderDetail("City", project.city)}
          {renderDetail("Company", project.company?.name)}
          {renderDetail("Country Code", project.country_code)}
          {renderDetail("Completion Date", project.completion_date)}
          {renderDetail("Created At", project.created_at)}
          {renderDetail("Updated At", project.updated_at)}
          {renderDetail("Time Zone", project.time_zone)}
          {renderDetail("Projected Finish Date", project.projected_finish_date)}
          {renderDetail("Total Value", project.total_value)}
        </Box>
      </DetailsHeader>

      <ContentCard
        elevation={1}
        p={3}
        className="project-details-container project-users-container"
        sx={{ ...detailCardSx, pt: 0 }}
      >
        <Tabs value={tabIndex} onChange={handleTabChange} aria-label="users-admins-tabs">
          <Tab label="Users" />
          {isAdmin && <Tab label="Admin Controls" />}
          {isAdmin && <Tab label="Permission Audit" />}
          {isAdmin && <Tab label="Expiring Access" />}
          {isAdmin && <Tab label="Change Logs" />}
        </Tabs>

        {visitedTabs.has(0) && (
          <Box hidden={tabIndex !== 0}>
            <UsersTab users={projectUsers || []} paginationKey={`users:project:${projectId}`} />
          </Box>
        )}

        {isAdmin && visitedTabs.has(1) && (
          <Box hidden={tabIndex !== 1}>
            <AdminTab
              variant="project"
              users={projectUsers || []}
              thresholdDays={thresholdDaysProject}
              inputDays={inputDays}
              onInputChange={handleInputChange}
              onSubmit={handleSubmit}
              showConfirmDialog={showConfirm}
              showResetButton={true}
              thresholdDaysCompanyDefault={thresholdDaysCompanyDefault}
              onConfirm={handleConfirm}
              onCancel={handleCancel}
              onReset={handleReset}
              checkboxSelection={false}
              handleRestoreUser={handleRestoreUser}
              paginationKey={`admin:project:${projectId}`}
            />
          </Box>
        )}

        {isAdmin && visitedTabs.has(2) && (
          <Box hidden={tabIndex !== 2}>
            <PermissionAuditTab
              variant="project"
              users={projectUsers || []}
              projectName={project?.name}
              paginationKey={`audit:project:${projectId}`}
            />
          </Box>
        )}

        {isAdmin && visitedTabs.has(3) && (
          <Box hidden={tabIndex !== 3}>
            <ExpiringAccessTab
              companyId={companyIdForChangeLog}
              isAdmin={isAdmin}
              companyUsers={projectAssignedCompanyUsers}
              projects={[project]}
            companyThreshold={thresholdDaysCompanyDefault}
            paginationKey={`expiring:project:${projectId}`}
            onAfterRemove={refreshStorageData}
            />
          </Box>
        )}

        {isAdmin && visitedTabs.has(4) && (
          <Box hidden={tabIndex !== 4}>
            <ChangeLogsTab
              companyId={companyIdForChangeLog}
              projectName={project.name}
              refreshKey={changeLogRefreshKey}
              logUpdate={changeLogLiveUpdate}
            />
          </Box>
        )}
      </ContentCard>
    </div>
  );
}
